import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Box, Button, IconButton, Stack } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useKandidaatMutaties } from '../../api/kandidaten.ts';
import type { KandidaatProfiel, KandidaatTaal, Taalniveau, Vaardigheid, Vaardigheidsniveau } from '../../api/typen.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { VaardigheidVeld } from '../../componenten/keuzevelden.tsx';
import { Keuzeveld } from '../../componenten/velden.tsx';
import { opties, taalLabels, taalniveauLabels, vaardigheidsniveauLabels } from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface VaardigheidRij {
  vaardigheid: Vaardigheid | null;
  niveau: Vaardigheidsniveau | '';
}

interface VaardighedenWaarden {
  rijen: VaardigheidRij[];
}

const vaardigheidsniveauOpties = opties(vaardigheidsniveauLabels);
const taalniveauOpties = opties(taalniveauLabels);
const taalOpties = opties(taalLabels);

/**
 * Vervangt de vaardigheden van een kandidaat, elk met een niveau.
 * @param props De kandidaat en zijn huidige vaardigheden.
 * @returns De dialoog.
 */
export function VaardighedenFormulier({
  open,
  onSluit,
  kandidaatId,
  bestaand,
}: {
  open: boolean;
  onSluit: () => void;
  kandidaatId: string;
  bestaand: KandidaatProfiel['vaardigheden'];
}) {
  const mutaties = useKandidaatMutaties();
  const begin = (): VaardighedenWaarden => ({
    rijen: bestaand.map((koppeling) => ({ vaardigheid: koppeling.vaardigheid, niveau: koppeling.niveau ?? '' })),
  });
  const formulier = useForm<VaardighedenWaarden>({ defaultValues: begin() });
  const rijen = useFieldArray({ control: formulier.control, name: 'rijen' });
  useEffect(() => {
    if (open) {
      formulier.reset(begin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bestaand, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) =>
      mutaties.zetVaardigheden.mutateAsync({
        id: kandidaatId,
        invoer: waarden.rijen
          .filter((rij): rij is VaardigheidRij & { vaardigheid: Vaardigheid } => rij.vaardigheid !== null)
          .map((rij) => ({ vaardigheidId: rij.vaardigheid.id, niveau: rij.niveau || null })),
      }),
    { melding: 'Vaardigheden opgeslagen', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Vaardigheden"
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
      >
        <Stack spacing={2}>
          {rijen.fields.map((rij, index) => (
            <Stack key={rij.id} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ flex: 2 }}>
                <VaardigheidVeld
                  naam={`rijen.${index}.vaardigheid`}
                  verplicht
                  autoFocus={index === rijen.fields.length - 1}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Keuzeveld
                  naam={`rijen.${index}.niveau`}
                  label="Niveau"
                  opties={vaardigheidsniveauOpties}
                  leegLabel="Onbekend"
                />
              </Box>
              <IconButton aria-label="Rij verwijderen" onClick={() => rijen.remove(index)} sx={{ mt: 0.5 }}>
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => rijen.append({ vaardigheid: null, niveau: '' })}
            sx={{ alignSelf: 'flex-start' }}
          >
            Vaardigheid toevoegen
          </Button>
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}

interface TaalRij {
  taalcode: string;
  niveau: Taalniveau | '';
}

interface TalenWaarden {
  rijen: TaalRij[];
}

/**
 * Vervangt de talen van een kandidaat, elk met een niveau.
 * @param props De kandidaat en zijn huidige talen.
 * @returns De dialoog.
 */
export function TalenFormulier({
  open,
  onSluit,
  kandidaatId,
  bestaand,
}: {
  open: boolean;
  onSluit: () => void;
  kandidaatId: string;
  bestaand: readonly KandidaatTaal[];
}) {
  const mutaties = useKandidaatMutaties();
  const begin = (): TalenWaarden => ({
    rijen: bestaand.map((taal) => ({ taalcode: taal.taalcode, niveau: taal.niveau ?? '' })),
  });
  const formulier = useForm<TalenWaarden>({ defaultValues: begin() });
  const rijen = useFieldArray({ control: formulier.control, name: 'rijen' });
  useEffect(() => {
    if (open) {
      formulier.reset(begin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bestaand, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) =>
      mutaties.zetTalen.mutateAsync({
        id: kandidaatId,
        invoer: waarden.rijen
          .filter((rij) => rij.taalcode !== '')
          .map((rij) => ({ taalcode: rij.taalcode, niveau: rij.niveau || null })),
      }),
    { melding: 'Talen opgeslagen', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog open={open} titel="Talen" onSluit={onSluit} onVerzend={onVerzend} fout={fout} bezig={bezig}>
        <Stack spacing={2}>
          {rijen.fields.map((rij, index) => (
            <Stack key={rij.id} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Box sx={{ flex: 2 }}>
                <Keuzeveld naam={`rijen.${index}.taalcode`} label="Taal" opties={taalOpties} verplicht />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Keuzeveld
                  naam={`rijen.${index}.niveau`}
                  label="Niveau"
                  opties={taalniveauOpties}
                  leegLabel="Onbekend"
                />
              </Box>
              <IconButton aria-label="Rij verwijderen" onClick={() => rijen.remove(index)} sx={{ mt: 0.5 }}>
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() => rijen.append({ taalcode: '', niveau: '' })}
            sx={{ alignSelf: 'flex-start' }}
          >
            Taal toevoegen
          </Button>
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}
